// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive, ResponsiveType} from '@supermove/hooks';
import {OrganizationModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import EditPanel from '@shared/design/components/Panel/EditPanel';
import Text from '@shared/design/components/Text';
import ExcludedPhoneNumbersPanel from '@shared/modules/CallrailIntegration/components/ExcludedPhoneNumbersPanel';
import CallrailIntegrationMode from '@shared/modules/CallrailIntegration/enums/CallrailIntegrationMode';
import SetupCallrailIntegrationStep from '@shared/modules/CallrailIntegration/enums/SetupCallrailIntegrationStep';
import {FormikSetupCallrailIntegrationFormType} from '@shared/modules/CallrailIntegration/forms/SetupCallrailIntegrationForm';

const Container = Styled.View`
  height: 100%;
`;

interface PanelProps {
  title: string;
  BodyComponent: React.ComponentType<any>;
  handleEdit: () => void;
}

const Panel = ({title, BodyComponent, handleEdit}: PanelProps) => {
  return (
    <ActionPanel
      title={title}
      ActionButtonComponent={() => (
        <EditPanel.EditButton handleEdit={handleEdit} isEditing={false} isDisabled={false} />
      )}
      BodyComponent={BodyComponent}
    />
  );
};

const ApiKeyPanel = ({
  form,
  field,
  setCurrentStepKind,
  responsive,
}: {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  setCurrentStepKind: (kind: string) => void;
  responsive: ResponsiveType;
}) => {
  const kind = SetupCallrailIntegrationStep.API_KEY;
  const step = SetupCallrailIntegrationStep.getStep(kind);
  const mode = _.get(form.values, `${field}.mode`);

  const modeLabel =
    mode === CallrailIntegrationMode.CREATE_CALLS
      ? 'Create calls from CallRail data'
      : 'Enhance existing calls with CallRail data';

  return (
    <Panel
      title={step.title}
      handleEdit={() => setCurrentStepKind(kind)}
      BodyComponent={() => {
        return (
          <React.Fragment>
            <Text.Label style={{color: colors.gray.secondary}}>API Key</Text.Label>
            <Space height={8} />
            <Text.Body>•••••••••••••••••••</Text.Body>
          </React.Fragment>
        );
      }}
    />
  );
};

const AccountAndCompanyPanel = ({
  form,
  field,
  setCurrentStepKind,
  responsive,
}: {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  setCurrentStepKind: (kind: string) => void;
  responsive: ResponsiveType;
}) => {
  const kind = SetupCallrailIntegrationStep.ACCOUNT_AND_COMPANY;
  const step = SetupCallrailIntegrationStep.getStep(kind);

  // Check for company name error
  const companyError = _.get(form.errors, `${field}.callrailCompanyName`);
  const hasCompanyError = Boolean(companyError);

  return (
    <Panel
      title={step.title}
      handleEdit={() => setCurrentStepKind(kind)}
      BodyComponent={() => {
        return (
          <React.Fragment>
            <Text.Label style={{color: colors.gray.secondary}}>Account</Text.Label>
            <Space height={8} />
            <Text.Body>{_.get(form.values, `${field}.callrailAccountName`)}</Text.Body>
            <Space height={16} />
            <Text.Label style={{color: colors.gray.secondary}}>Company</Text.Label>
            <Space height={8} />
            <Text.Body>{_.get(form.values, `${field}.callrailCompanyName`)}</Text.Body>
            {hasCompanyError && (
              <React.Fragment>
                <Space height={8} />
                <Text.Body style={{color: colors.red.warning}}>{companyError}</Text.Body>
              </React.Fragment>
            )}
          </React.Fragment>
        );
      }}
    />
  );
};

const ProjectSettingsPanel = ({
  form,
  field,
  setCurrentStepKind,
  responsive,
  organization,
}: {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  setCurrentStepKind: (kind: string) => void;
  responsive: ResponsiveType;
  organization: OrganizationModel;
}) => {
  const kind = SetupCallrailIntegrationStep.PROJECT_SETTINGS;
  const step = SetupCallrailIntegrationStep.getStep(kind);
  const mode = _.get(form.values, `${field}.mode`);
  const isCreateCallsMode = mode === CallrailIntegrationMode.CREATE_CALLS;

  if (!isCreateCallsMode) {
    return null;
  }

  // Check for errors
  const projectTypeError = _.get(form.errors, `${field}.projectTypeId`);
  const jobTypeError = _.get(form.errors, `${field}.jobTypeId`);
  const hasProjectTypeError = Boolean(projectTypeError);
  const hasJobTypeError = Boolean(jobTypeError);

  const projectTypeId = _.get(form.values, `${field}.projectTypeId`);
  const projectType = _.find(
    organization.projectTypes,
    (pt) => String(pt.id) === String(projectTypeId),
  );

  const jobTypeId = _.get(form.values, `${field}.jobTypeId`);
  const jobType =
    projectType && _.find(projectType.jobTypes, (jt) => String(jt.id) === String(jobTypeId));

  return (
    <Panel
      title={step.title}
      handleEdit={() => setCurrentStepKind(kind)}
      BodyComponent={() => {
        return (
          <React.Fragment>
            <Text.Label style={{color: colors.gray.secondary}}>Project Type</Text.Label>
            <Space height={8} />
            <Text.Body>{projectType?.name || 'Not selected'}</Text.Body>
            {hasProjectTypeError && (
              <React.Fragment>
                <Space height={8} />
                <Text.Body style={{color: colors.red.warning}}>{projectTypeError}</Text.Body>
              </React.Fragment>
            )}
            <Space height={16} />
            <Text.Label style={{color: colors.gray.secondary}}>Job Type</Text.Label>
            <Space height={8} />
            <Text.Body>{jobType?.name || 'Not selected'}</Text.Body>
            {hasJobTypeError && (
              <React.Fragment>
                <Space height={8} />
                <Text.Body style={{color: colors.red.warning}}>{jobTypeError}</Text.Body>
              </React.Fragment>
            )}
          </React.Fragment>
        );
      }}
    />
  );
};

interface CallrailReviewFieldsProps {
  form: FormikSetupCallrailIntegrationFormType;
  field: string;
  setCurrentStepKind: (value: string | ((prevVar: string) => string)) => void;
  organization: OrganizationModel;
}

const CallrailReviewFields = ({
  form,
  field,
  setCurrentStepKind,
  organization,
}: CallrailReviewFieldsProps) => {
  const responsive = useResponsive();
  const responsiveHeight = responsive.desktop ? 16 : 0;
  const mode = _.get(form.values, `${field}.mode`);
  const isCreateCallsMode = mode === CallrailIntegrationMode.CREATE_CALLS;

  return (
    <Container style={responsive.desktop ? {} : {backgroundColor: colors.white}}>
      <ApiKeyPanel
        form={form}
        field={field}
        setCurrentStepKind={setCurrentStepKind}
        responsive={responsive}
      />
      <Space height={responsiveHeight} />
      <AccountAndCompanyPanel
        form={form}
        field={field}
        setCurrentStepKind={setCurrentStepKind}
        responsive={responsive}
      />
      {isCreateCallsMode && (
        <React.Fragment>
          <Space height={responsiveHeight} />
          <ProjectSettingsPanel
            form={form}
            field={field}
            setCurrentStepKind={setCurrentStepKind}
            responsive={responsive}
            organization={organization}
          />
          <Space height={responsiveHeight} />
          <ExcludedPhoneNumbersPanel
            form={form}
            field={field}
            setCurrentStepKind={setCurrentStepKind}
            responsive={responsive}
          />
        </React.Fragment>
      )}
    </Container>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CallrailReviewFields.fragment = gql`
  fragment CallrailReviewFields on Organization {
    id
    projectTypes: projectTypesForCategory(category: "MOVE") {
      id
      name
      jobTypes {
        id
        name
      }
    }
  }
`;

export default CallrailReviewFields;
